import { useMemo, useState } from 'react';
import { nameList } from '@/mocks/home';

const trendIcon: Record<string, string> = {
  up: 'ri-arrow-up-line text-primary-600',
  down: 'ri-arrow-down-line text-secondary-600',
  flat: 'ri-subtract-line text-foreground-400',
};

type SortKey = 'heat' | 'comments';

export default function NameListSection() {
  const [sortKey, setSortKey] = useState<SortKey>('heat');

  const rows = useMemo(
    () => [...nameList].sort((a, b) => b[sortKey] - a[sortKey]),
    [sortKey],
  );

  const options: { key: SortKey; label: string }[] = [
    { key: 'heat', label: '按热度' },
    { key: 'comments', label: '按讨论量' },
  ];

  return (
    <section id="list" className="bg-background-100/60 border-b border-background-200">
      <div className="mx-auto max-w-[1240px] px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-900 bg-secondary-100 px-3 py-1 rounded-full">
              <i className="ri-list-ordered" />
              榜单目录
            </span>
            <h2 className="mt-3 font-heading text-xl md:text-2xl font-black text-foreground-950">
              网红黑料名单
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              这份名单按近 7 天的讨论量滚动更新，只做索引，不代表对任何一方的定性。
              点击任意一行可以进入对应的档案合集。
            </p>

            <div className="mt-5 inline-flex items-center gap-1 p-1 rounded-full border border-background-200 bg-background-50">
              {options.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={
                    sortKey === option.key
                      ? 'px-4 py-1.5 rounded-full bg-secondary-500 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer'
                      : 'px-4 py-1.5 rounded-full text-xs text-foreground-600 hover:text-foreground-950 whitespace-nowrap cursor-pointer'
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-xs text-foreground-600 leading-relaxed">
                <i className="ri-shield-check-line mr-1 text-secondary-700" />
                名单仅统计公开讨论数据，涉及个人隐私的信息不会收录。
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 text-[11px] text-foreground-500 border-b border-background-200 bg-background-100/70">
                <span className="col-span-1">排名</span>
                <span className="col-span-5">名单条目</span>
                <span className="col-span-2">领域</span>
                <span className="col-span-2">热度</span>
                <span className="col-span-2 text-right">讨论量</span>
              </div>
              <ul className="divide-y divide-background-200">
                {rows.map((row, index) => (
                  <li
                    key={row.codename}
                    className="grid grid-cols-12 gap-3 items-center px-4 py-3.5 hover:bg-background-100/70 transition-colors cursor-pointer"
                  >
                    <span className="col-span-2 sm:col-span-1">
                      <span
                        className={
                          index < 3
                            ? 'inline-flex w-7 h-7 items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-xs font-bold'
                            : 'inline-flex w-7 h-7 items-center justify-center rounded-md bg-background-200 text-foreground-700 font-heading text-xs font-bold'
                        }
                      >
                        {index + 1}
                      </span>
                    </span>
                    <span className="col-span-10 sm:col-span-5 min-w-0">
                      <span className="block text-sm font-medium text-foreground-950 truncate">{row.codename}</span>
                      <span className="sm:hidden inline-flex items-center gap-2 mt-0.5 text-[11px] text-foreground-500">
                        {row.field} · 热度 {row.heat}
                      </span>
                    </span>
                    <span className="hidden sm:block col-span-2 text-xs text-foreground-600">{row.field}</span>
                    <span className="hidden sm:flex col-span-2 items-center gap-1.5">
                      <span className="font-heading text-sm font-bold text-foreground-950">{row.heat}</span>
                      <i className={`${trendIcon[row.trend] ?? trendIcon.flat} text-sm`} />
                    </span>
                    <span className="hidden sm:block col-span-2 text-right text-xs text-foreground-600">
                      {row.comments}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}